/**
 * Searching and grouping the catalog. Pure functions, no React, so the rules
 * are readable in one place and testable without a DOM.
 */

import type { RoomSummary } from '@era/shared'
import { peopleOf } from './people'

/* -------------------------------------------------------------------------- */
/* filters                                                                     */
/* -------------------------------------------------------------------------- */

export type StatusFilter = 'all' | 'ready' | 'processing' | 'open' | 'unrecorded'

export const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'ready', label: 'Transcript ready' },
  { value: 'processing', label: 'Processing' },
  { value: 'open', label: 'Still open' },
  { value: 'unrecorded', label: 'Not recorded' },
]

export function matchesStatus(room: RoomSummary, filter: StatusFilter): boolean {
  switch (filter) {
    case 'ready':
      return room.has_transcript
    case 'processing':
      // A call that is OVER and whose transcript has not arrived yet. A room
      // that is still open belongs to "Still open" — it is not processing
      // anything, because processing starts when the last person leaves.
      //
      // `failed` and `purged` are excluded because they will never produce a
      // transcript, and a filter that promises "this is on its way" must not
      // include things that are not. `ended` is deliberately NOT excluded even
      // though it is non-terminal: that is precisely the state this bucket is
      // for.
      return (
        room.recording_ever_enabled &&
        !room.has_transcript &&
        room.status === 'closed' &&
        room.session_state !== 'failed' &&
        room.session_state !== 'purged'
      )
    case 'open':
      return room.status === 'open'
    case 'unrecorded':
      return !room.recording_ever_enabled
    case 'all':
    default:
      return true
  }
}

/**
 * Free-text search over everything a person might remember about a call.
 *
 * Includes the session id and the node deliberately: this is a reference client
 * for an API, and the person looking at it is as likely to be an operator
 * chasing `rtc-01` as a user looking for "Weekly check-in".
 *
 * It also searches the PEOPLE, which it could not do before
 * `participants_preview` existed: "person" in the placeholder used to mean
 * only the creator, so searching for the other person in a two-person call
 * returned nothing while their face was on the row. Same source as the row
 * draws from (`peopleOf`), so what is visible is what is searchable.
 */
export function matchesQuery(room: RoomSummary, needle: string): boolean {
  if (!needle) return true
  const haystack = [
    room.title ?? '',
    room.created_by_name,
    ...peopleOf(room).people.map((person) => person.name),
    room.join_code,
    room.session_id,
    room.node_name ?? '',
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}

/** A session id typed into the search box, for a session this app never made. */
export function looksLikeSessionId(input: string): boolean {
  return /^s_[A-Za-z0-9_-]{6,}$/.test(input.trim())
}

/* -------------------------------------------------------------------------- */
/* grouping                                                                    */
/* -------------------------------------------------------------------------- */

export interface HistoryGroup {
  key: string
  label: string
  rooms: RoomSummary[]
}

/**
 * Group by day, with the recent days named.
 *
 * "Yesterday, 14:20" is how people actually hold a meeting in their head;
 * "8 Aug 2026, 14:20" is how a database holds it. The named groups run out
 * after a week, at which point the date genuinely is the most useful label.
 */
export function groupByDay(rooms: RoomSummary[], now = new Date()): HistoryGroup[] {
  const groups: HistoryGroup[] = []
  const byKey = new Map<string, HistoryGroup>()

  for (const room of rooms) {
    const date = new Date(room.created_at)
    const key = Number.isNaN(date.getTime()) ? 'unknown' : startOfDay(date).toISOString()
    let group = byKey.get(key)
    if (!group) {
      group = { key, label: labelFor(date, now), rooms: [] }
      byKey.set(key, group)
      groups.push(group)
    }
    group.rooms.push(room)
  }

  return groups
}

function startOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

function labelFor(date: Date, now: Date): string {
  if (Number.isNaN(date.getTime())) return 'Undated'
  const days = Math.round(
    (startOfDay(now).getTime() - startOfDay(date).getTime()) / (24 * 60 * 60 * 1000),
  )
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) {
    return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(date)
  }
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  }).format(date)
}

/* -------------------------------------------------------------------------- */
/* summary                                                                     */
/* -------------------------------------------------------------------------- */

export interface HistoryTotals {
  calls: number
  /** Summed `duration_ms`. Null durations are unknown, not zero, so they are skipped. */
  recordedMs: number
  transcripts: number
  processing: number
}

export function totals(rooms: RoomSummary[]): HistoryTotals {
  return rooms.reduce<HistoryTotals>(
    (accumulator, room) => ({
      calls: accumulator.calls + 1,
      recordedMs: accumulator.recordedMs + (room.duration_ms ?? 0),
      transcripts: accumulator.transcripts + (room.has_transcript ? 1 : 0),
      processing: accumulator.processing + (matchesStatus(room, 'processing') ? 1 : 0),
    }),
    { calls: 0, recordedMs: 0, transcripts: 0, processing: 0 },
  )
}
